import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { isAlphaLabMode } from './PublicArchiveBanner';

const T = {
  ink: '#111111',
  muted: '#666660',
  line: '#e5e3df',
  accent: '#8B2500',
  bg: '#f7f6f3',
  mono: '"JetBrains Mono", "Courier New", monospace',
};

const LINK_MAP = {
  home: '/',
  alpha: '/alpha-platform',
  desk: '/desk',
  board: '/desk',
  catalyst: '/desk#catalyst-trades',
  trackrecord: '/track-record',
  methodology: '/methodology',
};

function normalizeActive(value) {
  if (value === 'board' || value === 'catalyst') return 'desk';
  return value || 'home';
}

function linkStyle(active) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '40px',
    padding: '0 4px',
    textDecoration: 'none',
    fontSize: '14px',
    color: active ? T.ink : T.muted,
    fontWeight: active ? 700 : 500,
    background: 'transparent',
    border: 'none',
    borderBottom: active ? `2px solid ${T.accent}` : '2px solid transparent',
  };
}

export default function PublicSiteHeader({
  active = 'home',
  subtitle = 'The macro research desk for self-directed investors',
  showLogin = true,
  contextLabel = '',
  contextHref = '',
  contextText = '',
  currentPath = '',
  variant = 'default',
}) {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [menuOpen, setMenuOpen] = useState(false);
  const activeKey = normalizeActive(active);

  useEffect(() => {
    const onResize = () => {
      const nextIsMobile = window.innerWidth <= 768;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const resolvedPath = currentPath || LINK_MAP[active] || LINK_MAP[activeKey] || '/';
  const loginHref = `/?login=1&returnTo=${encodeURIComponent(resolvedPath)}`;
  const deskHref = LINK_MAP.desk;
  const isReading = variant === 'reading';
  const alphaLabMode = isAlphaLabMode();
  const showSubtitle = Boolean(subtitle) && !isReading && !isMobile;
  const showContext = Boolean(contextLabel || contextText) && !isReading;
  const navItems = alphaLabMode
    ? [
      ['Home', '/', activeKey === 'home'],
      ['Alpha OS', '/alpha-platform', activeKey === 'alpha'],
      ['Methodology', '/methodology', activeKey === 'methodology'],
    ]
    : [
      ['Home', '/', activeKey === 'home'],
      ['Alpha OS', '/alpha-platform', activeKey === 'alpha'],
      ['Desk', deskHref, activeKey === 'desk'],
      ['Track Record', '/track-record', activeKey === 'trackrecord'],
      ['Methodology', '/methodology', activeKey === 'methodology'],
    ];

  function navLinkStyle(activeState) {
    const base = linkStyle(activeState);
    return {
      ...base,
      minHeight: isReading ? '32px' : base.minHeight,
      fontSize: isReading ? '13px' : base.fontSize,
      fontWeight: activeState ? 600 : (isReading ? 450 : base.fontWeight),
      borderBottom: isReading ? '2px solid transparent' : base.borderBottom,
      opacity: activeState ? 1 : (isReading ? 0.84 : 1),
    };
  }

  return (
    <header style={{
      borderBottom: `1px solid ${T.line}`,
      background: isReading ? 'rgba(247,246,243,0.88)' : 'rgba(247,246,243,0.94)',
      backdropFilter: isReading ? 'blur(8px)' : 'blur(12px)',
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      <div style={{
        maxWidth: '1120px',
        margin: '0 auto',
        padding: isReading
          ? (isMobile ? '10px 14px' : '11px 24px')
          : (isMobile ? '12px 14px' : '14px 24px'),
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          flexWrap: 'wrap',
        }}>
          <div>
            <a href="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              <img
                src="/brand/logo-wordmark.svg"
                alt="Moods AI"
                style={{ height: isReading ? (isMobile ? '20px' : '22px') : (isMobile ? '22px' : '24px'), width: 'auto', display: 'block' }}
              />
            </a>
            {showSubtitle ? <div style={{ fontSize: '13px', color: T.muted, marginTop: '2px' }}>{subtitle}</div> : null}
            {showContext && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: T.muted, fontFamily: T.mono, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {contextHref && contextLabel ? (
                  <a href={contextHref} style={{ color: T.accent, textDecoration: 'none' }}>
                    {contextLabel}
                  </a>
                ) : contextLabel ? (
                  <span>{contextLabel}</span>
                ) : null}
                {contextHref && contextLabel && contextText ? <span style={{ color: '#b0a89c' }}>/</span> : null}
                {contextText ? <span>{contextText}</span> : null}
              </div>
            )}
          </div>

          {isMobile ? (
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '42px',
                height: '42px',
                border: `1px solid ${T.line}`,
                background: '#fff',
                color: T.ink,
                cursor: 'pointer',
              }}
            >
              {menuOpen ? <X size={18} strokeWidth={1.8} /> : <Menu size={18} strokeWidth={1.8} />}
            </button>
          ) : null}

          {!isMobile && (
            <nav style={{ display: 'flex', alignItems: 'center', gap: isReading ? '14px' : '18px' }}>
              {navItems.map(([label, href, itemActive]) => (
                <a key={href} href={href} style={navLinkStyle(itemActive)}>{label}</a>
              ))}
              {showLogin && (
                isReading ? (
                  <a href={loginHref} style={{
                    color: T.muted,
                    textDecoration: 'none',
                    fontFamily: T.mono,
                    fontSize: '12px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}>
                    Login
                  </a>
                ) : (
                  <a href={loginHref} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: T.accent,
                    border: `1px solid ${T.accent}`,
                    color: '#fff',
                    fontFamily: T.mono,
                    fontSize: '13px',
                    minHeight: '40px',
                    padding: '0 14px',
                    textDecoration: 'none',
                  }}>
                    Login
                  </a>
                )
              )}
            </nav>
          )}
        </div>

        {isMobile && menuOpen && (
          <div style={{ display: 'grid', gap: isReading ? '6px' : '8px', marginTop: isReading ? '8px' : '10px' }}>
            <nav
              aria-label="Primary"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
              }}
            >
              {navItems.map(([label, href, itemActive]) => (
                <a key={href} href={href} style={{ ...navLinkStyle(itemActive), minHeight: '44px', fontSize: '13px', padding: '0 12px', justifyContent: 'flex-start', border: `1px solid ${itemActive ? T.accent : T.line}`, background: itemActive ? 'rgba(139,37,0,0.05)' : '#fff' }}>{label}</a>
              ))}
              {showLogin ? (
                <a href={loginHref} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: '44px',
                  padding: '0 12px',
                  textDecoration: 'none',
                  background: T.accent,
                  color: '#fff',
                  fontFamily: T.mono,
                  fontSize: '12px',
                  border: `1px solid ${T.accent}`,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}>
                  Login
                </a>
              ) : null}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
